import Chart from 'chart.js/auto';
import { $, $$, debounce } from '@/utilities/helpers';

Chart.defaults.font.family = '"gotham", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

const getAllNumbersArray = (data) => data.flatMap(d => [d.fund, d.average]);

const getMax = (dataset, tickMax, increment) => {
    if (tickMax !== null) return tickMax;
    return Math.ceil(Math.max(...getAllNumbersArray(dataset)) / increment) * increment + increment;
};

const getMin = (dataset, tickMin, increment) => {
    if (tickMin !== null) return tickMin;
    return Math.floor(Math.min(...getAllNumbersArray(dataset)) / increment) * increment;
};

export const initLineChart = ($chart) => {

    $chart.style.minHeight = '200px';

    let dataset = JSON.parse($chart.dataset.dataset);

    let tickMin = null;
    let tickMax = null;
    let tickIncrement = 5;

    const min = getMin(dataset, tickMin, tickIncrement);
    const max = getMax(dataset, tickMax, tickIncrement);

    // Pad with empty labels/null values after data so points are never at the right edge (mobile only)
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const MIN_POINTS = 5;
    const padAfter = viewportWidth >= 968 ? Math.max(0, MIN_POINTS - dataset.length) : 0;
    const $lineChartWrap = $chart.closest('.line-chart__wrap') || $chart.closest('.inline-chart__wrapper').parentElement;
    const $inlineWrap = $chart.closest('.line-chart__wrap');

    if ($inlineWrap && viewportWidth < 968 && dataset.length < MIN_POINTS) {
        $inlineWrap.classList.add('no-five-year-data-available');
    }

    const paddedLabels = [...dataset.map(item => item.year), ...Array(padAfter).fill('')];
    const paddedFund = [...dataset.map(item => item.fund), ...Array(padAfter).fill(null)];
    const paddedAverage = [...dataset.map(item => item.average), ...Array(padAfter).fill(null)];

    let data = {
        labels: paddedLabels,
        datasets: [
            {
                label: 'This fund',
                tension: 0,
                data: paddedFund,
                fill: false,
                pointStyle: 'circle',
                borderWidth: 2,
                pointBorderWidth: 3,
                pointRadius: 5,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: '#548200',
                borderColor: '#548200',
                yAxisID: 'left-y-axis',
                clip: false,
            },
            {
                label: 'Average',
                tension: 0,
                data: paddedAverage,
                fill: false,
                pointStyle: 'circle',
                borderWidth: 2,
                pointBorderWidth: 3,
                pointRadius: 5,
                pointBackgroundColor: '#565A61',
                borderColor: '#565A61',
                yAxisID: 'right-y-axis',
                clip: false,
            },
        ],
    };

    let chart = new Chart($chart, {
        type: 'line',
        data: data,
        options: {
            responsive: true,
            layout: { padding: 0 },
            plugins: {
                datalabels: { display: false },
                legend: { display: false },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: 'rgba(255,255,255,.8)',
                    borderColor: '#565A61',
                    borderWidth: 2,
                    cornerRadius: 3,
                    xPadding: 8,
                    yPadding: 8,
                    titleColor: '#000000',
                    bodyColor: '#000000',
                    animation: false,
                    boxPadding: 4,
                    usePointStyle: true,
                },
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    border: { display: false },
                    grid: {
                        display: false,
                        drawOnChartArea: false,
                        tickLength: 0,
                    },
                    ticks: { display: false },
                },
                'left-y-axis': {
                    position: 'left',
                    min,
                    max,
                    border: { display: false },
                    grid: {
                        color: '#ebf1f3',
                        drawBorder: false,
                        tickLength: 50,
                    },
                    ticks: {
                        stepSize: tickIncrement,
                        color: '#565A61',
                        padding: 12,
                        callback: (value) => value + '%',
                    },
                    afterFit: (scale) => { scale.width += 10; },
                },
                'right-y-axis': {
                    position: 'right',
                    min,
                    max,
                    border: { display: false },
                    grid: {
                        color: '#ebf1f3',
                        drawBorder: false,
                        tickLength: 50,
                    },
                    ticks: {
                        stepSize: tickIncrement,
                        color: '#565A61',
                        padding: 12,
                        callback: (value) => value + '%',
                    },
                    afterFit: (scale) => { scale.width += 10; },
                },
            },
        }
    });

    // Pad legend columns to match chart padding (min 5 columns)
    if (padAfter > 0 && $lineChartWrap) {
        const $rowAlts = $lineChartWrap.querySelectorAll('.line-chart__legend__row-alt');
        if ($rowAlts.length > 0) {
            // YearlyReturns structure: column-alt items inside a row-alt wrapper
            $rowAlts.forEach($rowAlt => {
                for (let i = 0; i < padAfter; i++) {
                    const $empty = document.createElement('div');
                    $empty.className = 'line-chart__legend__column-alt';
                    $rowAlt.appendChild($empty);
                }
            });
        } else {
            // CardFund structure: column items as direct children of each legend row
            $lineChartWrap.querySelectorAll('.line-chart__legend__row').forEach($row => {
                for (let i = 0; i < padAfter; i++) {
                    const $empty = document.createElement('div');
                    $empty.className = 'line-chart__legend__column';
                    $row.appendChild($empty);
                }
            });
        }
    }

    window.addEventListener('resize', debounce(() => chart.resize(), 300));

}

